import { type FC } from 'react';

import {
  CheckboxField,
  CheckboxGroupField,
  SelectField,
  Smokebreak,
} from '@wk/components-react16';

import type { SiteComplianceSite } from '@/domains/siteCompliance';
import {
  normalizeSiteComplianceDivision,
} from '@/domains/siteCompliance';
import MultiSelect from '@/shared/ui/MultiSelect';

import type { SiteComplianceFilters } from './SiteCompliancePage.types';

interface SiteComplianceFiltersProps {
  businessUnitOptions: string[];
  divisionOptions: string[];
  filters: SiteComplianceFilters;
  isMobileFiltersOpen: boolean;
  isMobileViewport: boolean;
  onCloseMobileFilters: () => void;
  onUpdateFilters: (updates: Partial<SiteComplianceFilters>) => void;
  regionOptions: string[];
  siteOptions: SiteComplianceSite[];
  siteTypeOptions: Array<{
    label: string;
    value: SiteComplianceFilters['siteType'];
  }>;
}

const FILTER_FIELD_CLASS = 'w-full sm:flex-1 sm:min-w-[150px]';

const SiteComplianceFilters: FC<SiteComplianceFiltersProps> = ({
  businessUnitOptions,
  divisionOptions,
  filters,
  isMobileFiltersOpen,
  isMobileViewport,
  onCloseMobileFilters,
  onUpdateFilters,
  regionOptions,
  siteOptions,
  siteTypeOptions,
}) => {
  const filtersContent = (
    <>
      <div className={FILTER_FIELD_CLASS}>
        <SelectField
          size="small"
          label="Primary Division"
          labelFor="site-compliance-divisions"
        >
          <select
            id="site-compliance-divisions"
            name="divisions"
            className="w-full"
            value={filters.divisions[0] ?? ''}
            onChange={(event) => {
              onUpdateFilters({
                divisions: event.target.value ? [event.target.value] : [],
              });
            }}
          >
            <option value="">Select divisions</option>
            {divisionOptions.map((division) => (
              <option key={division} value={division}>
                {division}
              </option>
            ))}
          </select>
        </SelectField>
      </div>

      <div className={`cg-field flex flex-col ${FILTER_FIELD_CLASS}`}>
        <label className="cg-field-label" htmlFor="site-compliance-search">
          Search
        </label>
        <MultiSelect
          size="small"
          className="w-full min-w-0 max-w-none"
          options={siteOptions.map((site) => ({
            label: site.siteName,
            value: site.siteId,
            render: (option) => (
              <span className="flex w-full flex-col gap-1" key={option.value}>
                <span className="w-full truncate text-sm font-medium">
                  {site.siteId} - {site.siteName}
                </span>
                <span className="text-xs text-muted-foreground">
                  {normalizeSiteComplianceDivision(
                    site.primaryDivision ?? 'Unknown',
                  )}{' '}
                  · {site.region}
                </span>
              </span>
            ),
          }))}
          value={filters.siteIds}
          onChange={(siteIds) => {
            onUpdateFilters({ siteIds });
          }}
          placeholder="Search by Site ID or Name"
          itemHeight={48}
        />
      </div>

      <div className={FILTER_FIELD_CLASS}>
        <SelectField size="small" label="Site Type" labelFor="site-compliance-type">
          <select
            id="site-compliance-type"
            name="siteType"
            className="w-full"
            value={filters.siteType}
            onChange={(event) => {
              onUpdateFilters({
                siteType: event.target.value as SiteComplianceFilters['siteType'],
              });
            }}
          >
            <option value="ALL">Select Site Type</option>
            {siteTypeOptions.map(({ label, value }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </SelectField>
      </div>

      <div className={FILTER_FIELD_CLASS}>
        <SelectField size="small" label="Region" labelFor="site-compliance-region">
          <select
            id="site-compliance-region"
            name="region"
            className="w-full"
            value={filters.region}
            onChange={(event) => {
              onUpdateFilters({ region: event.target.value });
            }}
          >
            <option value="">Select Region</option>
            {regionOptions.map((region) => (
              <option key={region} value={region}>
                {region}
              </option>
            ))}
          </select>
        </SelectField>
      </div>

      <div className={FILTER_FIELD_CLASS}>
        <SelectField
          size="small"
          label="Business Unit"
          labelFor="site-compliance-business-unit"
        >
          <select
            id="site-compliance-business-unit"
            name="businessUnit"
            className="w-full"
            value={filters.businessUnit}
            onChange={(event) => {
              onUpdateFilters({ businessUnit: event.target.value });
            }}
          >
            <option value="">Select Business Unit</option>
            {businessUnitOptions.map((businessUnit) => (
              <option key={businessUnit} value={businessUnit}>
                {businessUnit}
              </option>
            ))}
          </select>
        </SelectField>
      </div>

      <div className="flex w-full flex-col justify-end sm:w-auto sm:shrink-0">
        <CheckboxGroupField size="small">
          <div className="flex flex-col gap-1">
            <CheckboxField label="Flagship Location Y/N">
              <input
                name="flagshipOnly"
                type="checkbox"
                checked={filters.flagshipOnly}
                onChange={(event) =>
                  onUpdateFilters({ flagshipOnly: event.target.checked })
                }
              />
            </CheckboxField>
            <CheckboxField label="Enhanced Network Y/N">
              <input
                name="enhancedOnly"
                type="checkbox"
                checked={filters.enhancedOnly}
                onChange={(event) =>
                  onUpdateFilters({ enhancedOnly: event.target.checked })
                }
              />
            </CheckboxField>
          </div>
        </CheckboxGroupField>
      </div>
    </>
  );

  if (isMobileViewport) {
    return (
      <Smokebreak
        isOpen={isMobileFiltersOpen}
        position="right"
        toFocus='[data-e2e="cg-smokebreak-close"]'
        onUserRequest={onCloseMobileFilters}
      >
        <h2 slot="smokebreakHeader" className="p-3 text-lg font-semibold">
          Filters
        </h2>
        <div className="p-3" slot="smokebreakBody">
          <div className="site-compliance-filters grid w-full grid-cols-1 gap-3">
            {filtersContent}
          </div>
        </div>
      </Smokebreak>
    );
  }

  return (
    <div
      className="site-compliance-filters grid w-full grid-cols-1 gap-3 sm:flex sm:items-end sm:gap-x-4 sm:gap-y-1 sm:overflow-x-auto sm:scrollbar sm:scrollbar-h-1 sm:scrollbar-thumb-[#005b92] sm:scrollbar-track-gray-300 sm:pb-1"
    >
      {filtersContent}
    </div>
  );
};

export default SiteComplianceFilters;
